import React, { useRef, useState } from "react";
import { Duration, User } from "../../../features/deletePJP/deletePJPTypes";
import Autocomplete from "@mui/material/Autocomplete";
import { Loader } from "../../loader/Loader";
import TextField from "@mui/material/TextField";
import { fetchUserPJPData, rejectPjp } from "../../../features/deletePJP/deletePJPService";
import {
  TranslationEnum,
  usePortalTranslation,
} from "../../../providers/TranslationProvider";
import { InfoOutlined } from "@material-ui/icons";
import NoDataMessage from "../../manageEvents/NoEventsMessage";
import PjpSummaryCard from "../../pjpApproval/PjpSummaryCard";
import {
  transformDayCard,
  transformPjpSummary,
} from "../../../features/pjpApproval/PjpApprovalServices";
import "./usersInfo.css";
import { Button, Box } from "@mui/material";
import { openPopup } from "../../../stateManagement/actions/popupActions";
type Props = {
  users: User[];
  duration: Duration;
  pjpPlanOption: any;
  getUsers : (duration: Duration,showPopup: boolean) => void;
};
const UsersInfo: React.FC<Props> = ({
  users,
  duration,
  pjpPlanOption,
  getUsers
}: Props) => {
  const { translate } = usePortalTranslation();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const summaryCardData = useRef<any>(null);
  const dayData = useRef<any>(null);
  const pjpData = useRef<any>(null);
  const [selectedDesignations, setSelectedDesignations] = useState<string[]>([]);
  const designationOptions = React.useMemo(() => {
    const vals = new Set<string>();
    (users || []).forEach((u) => {
      const d = typeof (u as any)?.designation === "string" ? (u as any).designation.trim() : "";
      if (d) vals.add(d.toLowerCase());
    });
    return Array.from(vals);
  }, [users]);
  const filteredUsers = React.useMemo(() => {
    const selected = new Set((selectedDesignations || []).map((d) => d.toLowerCase()));
    return (users || []).filter((u) => {
      const d = typeof (u as any)?.designation === "string" ? (u as any).designation.trim().toLowerCase() : "";
      return selected.size === 0 || (d && selected.has(d));
    });
  }, [users, selectedDesignations]);
  const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };
  const handleRejectPjp = async () => {
    if (!message) {
      openPopup("Alert",translate(TranslationEnum.delete_pjp,"Please fill in manager comments before submitting"),()=>{});
      setSubmitAttempted(true);
      return;
    }
    try {
      const response = await rejectPjp(pjpData.current,message)
      if(response.status === "Success")
      {
        openPopup("Success",translate(TranslationEnum.delete_pjp,"PJP Rejected Successfully"),()=>{});
      }
      getUsers(duration,false);
    
    } catch (error) {
      openPopup("Alert",translate(TranslationEnum.delete_pjp,"Error rejecting PJP"),()=>{});
      console.error("Error rejecting PJP", error);
    }
  };
  const handleUserChange = async (newValue: User | null) => {
    setSelectedUser(newValue);
    setMessage("");
    if (!newValue){
      setSubmitAttempted(false);
      return;
    }
    try {
      setLoading(true);
      pjpData.current = await fetchUserPJPData(newValue, duration);
      if(pjpData.current.length === 0){ 
        openPopup("Alert",translate(TranslationEnum.delete_pjp,"No PJP data available for the selected user"),()=>{});
        setLoading(false);
        return;
      }
      summaryCardData.current = transformPjpSummary(pjpData.current, pjpPlanOption,{ showRoutesErrorPopup: false });
      dayData.current = transformDayCard(pjpData.current, pjpPlanOption);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user pjp data:", error);
      openPopup("Alert",translate(TranslationEnum.delete_pjp,"Error fetching user pjp data"),()=>{});
      // setLoading(false);
    }
    finally{
      setLoading(false);
    }
  };
  return (
    <div className="deletePJP-userFilterAndInfo-wrapper">
      {users.length > 0 && <Box sx={{ display: "flex", gap: "20px", alignItems: "center"}}>
        {/* crate a multiselect mui dropdown for desiginaton  */}
        {/* only show the users , whose designations match with the selcted designations */}
        <Autocomplete
          sx={{ flex: 1 }}
          multiple
          disableCloseOnSelect
          clearOnBlur={false}
          selectOnFocus
          handleHomeEndKeys
          className="deletePJP-selectUser-input"
          options={designationOptions}
          isOptionEqualToValue={(o, v) => o === v}
          value={selectedDesignations}
          onChange={(event, value) => {
            const next = (value as string[]).map((v) => (v ?? "").toLowerCase()).filter(Boolean);
            setSelectedDesignations(next);
            // reset current selection if it no longer matches filter
            setSelectedUser(null);
            setMessage("");
            setSubmitAttempted(false);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={translate(TranslationEnum.delete_pjp,"Select Designations")}
            />
          )}
        />
        <Autocomplete
          sx={{ flex: 1 }}
          className="deletePJP-selectUser-input"
          value={selectedUser}
          options={filteredUsers}
          onChange={(event, newValue) => handleUserChange(newValue)} // Pass the selected user back to the handler
          getOptionLabel={(option: User) => {
            let value: string = option?.name ?? "";
            const loginId: string = option?.loginid ?? "";
            if (loginId.length > 0) {
              value += " (" + loginId + ")";
            }
            return value;
          }} // Display name in the dropdown
          renderInput={(params) => <TextField {...params} label={translate(TranslationEnum.delete_pjp,"Select User")} />}
        />
        <Box width="84px"/>
      </Box>}
      {/* loader */}
      
          {loading &&
          <div style={{
            height: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Loader />
          </div>
          }
          {!loading && (users.length===0 || (selectedUser && pjpData.current && pjpData.current.length===0) )&& (
            <div style={{
              height: '50vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <NoDataMessage
                icon={<InfoOutlined />}
                message={translate(
                  TranslationEnum.delete_pjp,
                  "No data available."
                )}
              />
            </div>
          )}
          {selectedUser && !loading && users.length > 0 && selectedUser && pjpData.current?.length>0 && (
            <>
              <div className="summaryCard">
                <PjpSummaryCard
                  summaryData={summaryCardData.current}
                  dayCardData={dayData.current}
                  comment={message}
                  onCommentChange={onMessageChange}
                  error={submitAttempted && !message}
                />
              </div>
              <div
                className="deletePJP-rejectPjp-Button"
              >
                <Button onClick={handleRejectPjp} variant="contained">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <span>
                      {translate(TranslationEnum.delete_pjp, "Reject PJP")}
                    </span>
                  </div>
                </Button>
              </div>
            </>
          )}
    </div>
  );
};

export default UsersInfo;
